import { Currency } from "@paywithglide/glide-js";

const STORAGE_KEY = "glide.lastTokenSelection";

export interface SavedTokenSelection {
  currencyId: string;
  currencySymbol: string;
  chainType: "evm" | "solana" | "bitcoin" | "tron";
  chainId?: string; // For specific EVM chain if applicable
  timestamp: number;
}

export function saveTokenSelection(
  currency: Currency,
  chainType: "evm" | "solana" | "bitcoin" | "tron",
  chainId?: string,
): void {
  try {
    const selection: SavedTokenSelection = {
      currencyId: currency.id,
      currencySymbol: currency.symbol,
      chainType,
      chainId,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch (error) {
    // Silently fail if localStorage is not available
    console.warn("Failed to save token selection to localStorage:", error);
  }
}

// Maps a CAIP-2 chain id (e.g. "solana:101") to the chain type used by the transfer flow
export function chainTypeFromCaip(
  chainId: string,
): SavedTokenSelection["chainType"] | undefined {
  const [namespace] = chainId.split(":");
  switch (namespace) {
    case "eip155":
      return "evm";
    case "solana":
      return "solana";
    case "bip122":
      return "bitcoin";
    case "tron":
      return "tron";
    default:
      return undefined;
  }
}

// Helper to save token selection from payment option (when Currency object is not available)
export function saveTokenSelectionFromPaymentOption(
  paymentCurrency: string, // CAIP-19 format: "eip155:1/erc20:0x..."
  currencySymbol: string,
): void {
  try {
    // Parse CAIP-19 to extract chain info
    const parts = paymentCurrency.split("/");
    if (parts.length < 2) return;

    const chainPart = parts[0]; // e.g., "eip155:1"
    const chainType = chainTypeFromCaip(chainPart);
    if (!chainType) return; // Unknown chain type

    const selection: SavedTokenSelection = {
      currencyId: paymentCurrency, // Use full CAIP-19 as ID
      currencySymbol,
      chainType,
      chainId: chainPart,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch (error) {
    console.warn("Failed to save token selection to localStorage:", error);
  }
}

export function loadTokenSelection(): SavedTokenSelection | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const selection = JSON.parse(stored) as SavedTokenSelection;

    // Optional: Expire after 30 days
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    if (Date.now() - selection.timestamp > thirtyDaysMs) {
      clearTokenSelection();
      return null;
    }

    return selection;
  } catch (error) {
    console.warn("Failed to load token selection from localStorage:", error);
    return null;
  }
}

export function clearTokenSelection(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear token selection from localStorage:", error);
  }
}
